import { useEffect, useMemo, useRef, useState } from "react";
import { DefaultChatTransport } from "ai";
import { useChat } from "@ai-sdk/react";
import chatLogoUrl from "./assets/chat_logo.svg";
import chatLogoBrightUrl from "./assets/chat_logo_black.svg";
import {
  buildSessionWorldRegistry,
  MessageBubble,
  type RenderableMessage,
} from "./components/MessageBubble";
import { authClient } from "./lib/auth-client";
import type { GlobalPoolState } from "./server/global-pool";
import type {
  ChatQuotaSnapshot,
  QuotaStatusSnapshot,
} from "./server/quota";

const MODE_HEAT_WINDOW_MS = 5 * 60 * 1000;
const MODE_RING_RADIUS = 18;
const MODE_RING_CIRCUMFERENCE = 2 * Math.PI * MODE_RING_RADIUS;
const MINIMUM_SEND_POINTS = 1;
const THEME_STORAGE_KEY = "astrolens-chat-theme";
const USER_QUOTA_MAX_POINTS = 15;
const GLOBAL_QUOTA_MAX_POINTS = 10_000;
const ASTROLENS_INFO_URL = "https://www.astrolens.dev/";
const ASTROLENS_FANBOX_URL = "https://beayon.fanbox.cc/";
const QUICK_PROMPTS = [
  "雨の夜に一人で落ち着ける都会ワールド",
  "Quest対応で入れる静かな自然系ワールド",
  "少人数で雑談しやすいローファイな部屋",
  "写真映えする幻想的な夜景ワールド",
  "作業用に長居しやすい静かな空間",
  "友達と集まりやすい広めのラウンジ",
  "深夜に散歩したくなるネオン街",
  "和風で落ち着ける温泉や旅館っぽい場所",
  "一人でぼーっとできる雨音のある部屋",
  "PCVR向けで没入感の強いSF空間",
  "Questでも軽く入れるチル系ワールド",
  "少人数で映画鑑賞できるシアター空間",
];
const QUICK_PROMPT_COUNT = 3;

type ThemeMode = "dark" | "light";

function parseOptionalDate(value: unknown) {
  if (typeof value !== "string" || !value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function pickRandomPrompts(prompts: string[], count: number) {
  const pool = [...prompts];

  for (let index = pool.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [pool[index], pool[swapIndex]] = [pool[swapIndex], pool[index]];
  }

  return pool.slice(0, Math.min(count, pool.length));
}

export default function App() {
  const [input, setInput] = useState("");
  const [themeMode, setThemeMode] = useState<ThemeMode>("dark");
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [lastSentAt, setLastSentAt] = useState<Date | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [userQuotaPopupOpen, setUserQuotaPopupOpen] = useState(false);
  const [globalQuotaPopupOpen, setGlobalQuotaPopupOpen] = useState(false);
  const [quotaSnapshot, setQuotaSnapshot] = useState<ChatQuotaSnapshot | null>(
    null
  );
  const [globalPoolSnapshot, setGlobalPoolSnapshot] =
    useState<GlobalPoolState | null>(null);
  const messagesRef = useRef<HTMLElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const userQuotaPopupRef = useRef<HTMLDivElement | null>(null);
  const globalQuotaPopupRef = useRef<HTMLDivElement | null>(null);
  const userScrollIntentRef = useRef(false);
  const {
    data: session,
    isPending: isSessionPending,
    error: sessionError,
  } = authClient.useSession();
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/chat",
      }),
    []
  );

  const { messages, sendMessage, status, error, stop } = useChat({
    transport,
  });
  const quickPrompts = useMemo(
    () => pickRandomPrompts(QUICK_PROMPTS, QUICK_PROMPT_COUNT),
    []
  );
  const sessionWorldRegistry = useMemo(
    () => buildSessionWorldRegistry(messages as RenderableMessage[]),
    [messages]
  );
  const hasVisibleStreamingReasoning = useMemo(() => {
    const lastAssistantMessage = [...(messages as RenderableMessage[])]
      .reverse()
      .find((message) => message.role === "assistant");

    return Boolean(
      lastAssistantMessage?.parts?.some(
        (part) => part.type === "reasoning" && part.text?.trim()
      )
    );
  }, [messages]);

  const isBusy = status === "submitted" || status === "streaming";
  const signedInUser = useMemo(() => {
    if (!session?.user) {
      return null;
    }

    if (!quotaSnapshot) {
      return session.user;
    }

    return {
      ...session.user,
      ...quotaSnapshot,
    };
  }, [quotaSnapshot, session?.user]);
  const isSignedIn = Boolean(signedInUser);
  const hasDailyQuotaForSend =
    (signedInUser?.dailyQuotaRemaining ?? 0) >= MINIMUM_SEND_POINTS;
  const hasGlobalQuotaForSend =
    (globalPoolSnapshot?.remainingPoints ?? MINIMUM_SEND_POINTS) >=
    MINIMUM_SEND_POINTS;
  const isComposerLockedByQuota =
    isSignedIn && (!hasDailyQuotaForSend || !hasGlobalQuotaForSend);
  const composerLockMessage = !isSignedIn
    ? "Discordログイン後に入力できます"
    : !hasDailyQuotaForSend
      ? "本日のクオータを使い切りました。6:00 JST にリセットされます"
      : !hasGlobalQuotaForSend
        ? "本日のグローバルクオータを使い切りました。しばらく待ってから再試行してください"
        : "探したい雰囲気や条件を入力";
  const dailyQuotaRatio = signedInUser
    ? Math.max(
        0,
        Math.min(
          (signedInUser.dailyQuotaRemaining ?? 0) /
            Math.max(signedInUser.dailyQuotaLimit ?? 0, 1),
          1
        )
      )
    : 0;
  const dailyQuotaPercent = Math.round(dailyQuotaRatio * 100);
  const userQuotaMeterRatio = Math.max(
    0,
    Math.min((signedInUser?.dailyQuotaRemaining ?? 0) / USER_QUOTA_MAX_POINTS, 1)
  );
  const globalQuotaMeterRatio = Math.max(
    0,
    Math.min(
      (globalPoolSnapshot?.remainingPoints ?? 0) / GLOBAL_QUOTA_MAX_POINTS,
      1
    )
  );
  const globalPoolStatus = useMemo(() => {
    if (!globalPoolSnapshot) {
      return null;
    }

    const remainingPoints = globalPoolSnapshot.remainingPoints;

    if (remainingPoints >= 5000) {
      return {
        label: "潤沢",
        className: "global-quota global-quota--ample",
      };
    }

    if (remainingPoints >= 1000) {
      return {
        label: "多",
        className: "global-quota global-quota--high",
      };
    }

    if (remainingPoints >= 500) {
      return {
        label: "少",
        className: "global-quota global-quota--low",
      };
    }

    return {
      label: "枯渇",
      className: "global-quota global-quota--critical",
    };
  }, [globalPoolSnapshot]);
  const globalQuotaMeterTone = useMemo(() => {
    const remainingPoints = globalPoolSnapshot?.remainingPoints ?? 0;

    if (remainingPoints >= 5000) {
      return "quota-popup__meter-fill--ample";
    }

    if (remainingPoints >= 1000) {
      return "quota-popup__meter-fill--high";
    }

    if (remainingPoints >= 500) {
      return "quota-popup__meter-fill--low";
    }

    return "quota-popup__meter-fill--critical";
  }, [globalPoolSnapshot?.remainingPoints]);
  const modeIsHeat =
    lastSentAt != null && now.getTime() - lastSentAt.getTime() < MODE_HEAT_WINDOW_MS;
  const modeLabel = modeIsHeat ? "Heat mode" : "Cool mode";
  const modeIcon = modeIsHeat ? "local_fire_department" : "ac_unit";
  const modeRemainingMs = modeIsHeat
    ? Math.max(MODE_HEAT_WINDOW_MS - (now.getTime() - lastSentAt!.getTime()), 0)
    : 0;
  const modeProgress = modeIsHeat ? modeRemainingMs / MODE_HEAT_WINDOW_MS : 0;
  const timerDashOffset =
    MODE_RING_CIRCUMFERENCE * (1 - Math.max(0, Math.min(modeProgress, 1)));
  const themeToggleLabel =
    themeMode === "light" ? "ダークモードに切り替え" : "ライトモードに切り替え";
  const themeLogoUrl = themeMode === "light" ? chatLogoBrightUrl : chatLogoUrl;

  function scrollToBottom(behavior: ScrollBehavior = "smooth") {
    const end = endRef.current;
    if (!end) {
      return;
    }

    end.scrollIntoView({
      behavior,
      block: "end",
    });
  }

  function isNearBottom(container: HTMLElement) {
    const threshold = 48;
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      threshold
    );
  }

  useEffect(() => {
    const container = messagesRef.current;
    if (!container || !autoScrollEnabled) {
      return;
    }

    scrollToBottom("smooth");
  }, [messages, isBusy, error, autoScrollEnabled]);

  useEffect(() => {
    const storedTheme =
      typeof window !== "undefined"
        ? window.localStorage.getItem(THEME_STORAGE_KEY)
        : null;

    if (storedTheme === "light" || storedTheme === "dark") {
      setThemeMode(storedTheme);
      return;
    }

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: light)").matches
    ) {
      setThemeMode("light");
    }
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
    document.documentElement.style.colorScheme = themeMode;
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 1_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const menu = accountMenuRef.current;
      if (menu && !menu.contains(target)) {
        setAccountMenuOpen(false);
      }

      const userQuotaPopup = userQuotaPopupRef.current;
      if (userQuotaPopup && !userQuotaPopup.contains(target)) {
        setUserQuotaPopupOpen(false);
      }

      const globalQuotaPopup = globalQuotaPopupRef.current;
      if (globalQuotaPopup && !globalQuotaPopup.contains(target)) {
        setGlobalQuotaPopupOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setAccountMenuOpen(false);
        setUserQuotaPopupOpen(false);
        setGlobalQuotaPopupOpen(false);
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setQuotaSnapshot(null);
      setGlobalPoolSnapshot(null);
      setAccountMenuOpen(false);
      setUserQuotaPopupOpen(false);
      setGlobalQuotaPopupOpen(false);
      return;
    }

    let cancelled = false;

    async function refreshQuota() {
      const response = await fetch("/api/quota", {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
        cache: "no-store",
      });

      if (!response.ok || cancelled) {
        return;
      }

      const quota = (await response.json()) as QuotaStatusSnapshot;
      if (!cancelled) {
        setQuotaSnapshot(quota.user);
        setGlobalPoolSnapshot(quota.globalPool);
      }
    }

    void refreshQuota();
    const intervalId = window.setInterval(() => {
      void refreshQuota();
    }, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [session?.user]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(textarea.scrollHeight, window.innerHeight * 0.3)}px`;
  }, [input]);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) {
      return;
    }
    const currentContainer = container;

    function markUserScrollIntent() {
      userScrollIntentRef.current = true;
    }

    function handleScroll() {
      if (!userScrollIntentRef.current) {
        return;
      }

      if (isNearBottom(currentContainer)) {
        setAutoScrollEnabled(true);
      } else {
        setAutoScrollEnabled(false);
      }

      userScrollIntentRef.current = false;
    }

    currentContainer.addEventListener("wheel", markUserScrollIntent, {
      passive: true,
    });
    currentContainer.addEventListener("touchmove", markUserScrollIntent, {
      passive: true,
    });
    currentContainer.addEventListener("pointerdown", markUserScrollIntent, {
      passive: true,
    });
    currentContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      currentContainer.removeEventListener("wheel", markUserScrollIntent);
      currentContainer.removeEventListener("touchmove", markUserScrollIntent);
      currentContainer.removeEventListener("pointerdown", markUserScrollIntent);
      currentContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  async function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isBusy || !isSignedIn || isComposerLockedByQuota) {
      return;
    }

    setAutoScrollEnabled(true);
    setInput("");
    const sentAt = new Date();
    setLastSentAt(sentAt);
    setNow(sentAt);
    await sendMessage({ text: trimmed });
  }

  function handleQuickPrompt(prompt: string) {
    setInput(prompt);
    textareaRef.current?.focus();
  }

  return (
    <div className="shell">
      <div className="shell__bg shell__bg--left" />
      <div className="shell__bg shell__bg--right" />

      <header className="hero">
        <div className="hero__header-row">
          <div className="hero__brand">
            <img
              alt="AstroLens Chat"
              className="hero__logo"
              src={themeLogoUrl}
            />
          </div>

          <div className="hero__auth">
            <button
              className="theme-toggle"
              type="button"
              aria-label={themeToggleLabel}
              title={themeToggleLabel}
              onClick={() => {
                setThemeMode((current) =>
                  current === "light" ? "dark" : "light"
                );
              }}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {themeMode === "light" ? "dark_mode" : "light_mode"}
              </span>
            </button>
            {isSignedIn ? (
              <>
                <div className="account-menu" ref={accountMenuRef}>
                  <button
                    className="auth-chip auth-chip--user auth-chip--button"
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={accountMenuOpen}
                    title="アカウントメニューを開く"
                    onClick={() => {
                      setAccountMenuOpen((current) => !current);
                    }}
                  >
                    {signedInUser?.image ? (
                      <img
                        alt={signedInUser.name ?? "Discord user"}
                        className="auth-chip__avatar"
                        src={signedInUser.image}
                      />
                    ) : (
                      <div className="auth-chip__avatar auth-chip__avatar--fallback">
                        {(signedInUser?.name ?? "A").slice(0, 1)}
                      </div>
                    )}
                    <div className="auth-chip__body">
                      <strong>{signedInUser?.name}</strong>
                      <span>{signedInUser?.accessTier ?? "free"} tier</span>
                    </div>
                    <span
                      className="material-symbols-outlined auth-chip__chevron"
                      aria-hidden="true"
                    >
                      {accountMenuOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                    </span>
                  </button>

                  {accountMenuOpen ? (
                    <div className="account-menu__dropdown" role="menu">
                      <button
                        className="account-menu__item"
                        type="button"
                        role="menuitem"
                        title="現在のアカウントからサインアウト"
                        onClick={() => {
                          setAccountMenuOpen(false);
                          void authClient.signOut();
                        }}
                      >
                        Sign out
                      </button>
                    </div>
                  ) : null}
                </div>

                {signedInUser ? (
                  <div className="quota-widget" ref={userQuotaPopupRef}>
                    <button
                      className="energy-meter energy-meter--button"
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={userQuotaPopupOpen}
                      aria-label="本日の残りエネルギー詳細を表示"
                      title={`本日このアカウントで使える残りエナジー: ${
                        signedInUser?.dailyQuotaRemaining ?? 0
                      }pt / ${signedInUser?.dailyQuotaLimit ?? 0}pt`}
                      style={{ ["--energy-fill" as string]: `${dailyQuotaPercent}%` }}
                      onClick={() => {
                        setUserQuotaPopupOpen((current) => !current);
                        setGlobalQuotaPopupOpen(false);
                      }}
                    >
                      <div className="energy-meter__glow" aria-hidden="true" />
                      <div className="energy-meter__main">
                        <div className="energy-meter__icon" aria-hidden="true">
                          <span className="material-symbols-outlined">bolt</span>
                        </div>

                        <div className="energy-meter__body">
                          <div className="energy-meter__value-row">
                            <strong>
                              {(signedInUser?.dailyQuotaRemaining ?? 0).toLocaleString()}
                              <span> pt</span>
                            </strong>
                          </div>
                        </div>
                      </div>
                    </button>

                    {userQuotaPopupOpen ? (
                      <div className="quota-popup" role="dialog" aria-label="ユーザークオータ詳細">
                        <div className="quota-popup__eyebrow">Your quota</div>
                        <strong className="quota-popup__value">
                          {(signedInUser?.dailyQuotaRemaining ?? 0).toLocaleString()}
                          <span> / {USER_QUOTA_MAX_POINTS} pt</span>
                        </strong>
                        <div
                          className="quota-popup__meter"
                          aria-hidden="true"
                        >
                          <span
                            className="quota-popup__meter-fill quota-popup__meter-fill--user"
                            style={{
                              width: `${Math.round(userQuotaMeterRatio * 100)}%`,
                            }}
                          />
                        </div>
                        <p className="quota-popup__caption">
                          AstroLens Chatの利用で消費される、あなた個人の1日あたりの残量です。
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {globalPoolStatus ? (
                  <div className="quota-widget" ref={globalQuotaPopupRef}>
                    <button
                      className={`${globalPoolStatus.className} global-quota--button`}
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={globalQuotaPopupOpen}
                      aria-label="グローバルクオータ詳細を表示"
                      title={`全体で利用できる残りグローバルクオータ: ${Math.round(
                        globalPoolSnapshot?.remainingPoints ?? 0
                      ).toLocaleString()}pt`}
                      onClick={() => {
                        setGlobalQuotaPopupOpen((current) => !current);
                        setUserQuotaPopupOpen(false);
                      }}
                    >
                      <span
                        className="material-symbols-outlined global-quota__icon"
                        aria-hidden="true"
                      >
                        public
                      </span>
                      <span className="global-quota__label">Global</span>
                      <strong>
                        {Math.round(globalPoolSnapshot?.remainingPoints ?? 0).toLocaleString()}
                        <span> pt</span>
                      </strong>
                    </button>

                    {globalQuotaPopupOpen ? (
                      <div className="quota-popup" role="dialog" aria-label="グローバルクオータ詳細">
                        <div className="quota-popup__eyebrow">Global quota</div>
                        <strong className="quota-popup__value">
                          {Math.round(
                            globalPoolSnapshot?.remainingPoints ?? 0
                          ).toLocaleString()}
                          <span> / {GLOBAL_QUOTA_MAX_POINTS.toLocaleString()} pt</span>
                        </strong>
                        <div
                          className="quota-popup__meter"
                          aria-hidden="true"
                        >
                          <span
                            className={`quota-popup__meter-fill ${globalQuotaMeterTone}`}
                            style={{
                              width: `${Math.round(globalQuotaMeterRatio * 100)}%`,
                            }}
                          />
                        </div>
                        <p className="quota-popup__caption">
                          全ユーザーで共有しているエナジー残量です。枯渇すると送信できません。
                        </p>
                        <div className="quota-popup__support">
                          <strong className="quota-popup__support-title">
                            グローバルエナジーを増やすには？
                          </strong>
                          <p className="quota-popup__support-body">
                            AstroLensは皆さんからの支援のおかげで無料で提供されています。
                            皆様からのFANBOX支援が、そのままサービスの運営の力になります！
                          </p>
                          <div className="quota-popup__support-actions">
                            <a
                              className="quota-popup__support-link quota-popup__support-link--ghost"
                              href={ASTROLENS_INFO_URL}
                              rel="noreferrer"
                              target="_blank"
                            >
                              もっと知る
                            </a>
                            <a
                              className="quota-popup__support-link"
                              href={ASTROLENS_FANBOX_URL}
                              rel="noreferrer"
                              target="_blank"
                            >
                              支援する
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <button
                className="auth-button"
                type="button"
                disabled={isSessionPending}
                title="Discordアカウントでログイン"
                onClick={() => {
                  void authClient.signIn.social({
                    provider: "discord",
                    callbackURL: window.location.href,
                  });
                }}
              >
                Discordでログイン
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="chat">
        <section className="chat__messages" ref={messagesRef}>
          {!isSignedIn ? (
            <div className="empty">
              <div className="empty__panel">
                <p className="empty__label">Explorer Access</p>
                <p className="empty__title">Discordログインで探索を開始</p>
                <p className="empty__body">
                  AstroLens Chatをご利用いただくには、ログインする必要があります。
                </p>
                {sessionError ? (
                  <p className="empty__error">
                    セッションの取得に失敗しました: {sessionError.message}
                  </p>
                ) : null}
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="empty">
              <div className="empty__panel">
                {/* <p className="empty__label">AstroLens Ready</p> */}
                <p className="empty__title">どんな世界を歩きたい気分？</p>
                <p className="empty__body">
                  雰囲気、人数、時間帯、Quest対応の有無などを、あなたの思うままに伝えてください。
                </p>
                <div className="empty__suggestions" aria-label="おすすめプロンプト">
                  {quickPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      className="empty__suggestion"
                      type="button"
                      onClick={() => handleQuickPrompt(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message as RenderableMessage}
              registry={sessionWorldRegistry}
            />
          ))}

          {error ? (
            <div className="status status--error">
              応答中にエラーが発生しました: {error.message}
            </div>
          ) : null}

          {isBusy && !hasVisibleStreamingReasoning ? (
            <div className="status status--thinking" aria-live="polite">
              <span className="status__thinking-text">思考中</span>
            </div>
          ) : isComposerLockedByQuota ? (
            <div className="status">
              {composerLockMessage}
            </div>
          ) : null}

          <div ref={endRef} />
        </section>

        {!autoScrollEnabled ? (
          <button
            className="chat__scroll-button"
            type="button"
            aria-label="最新メッセージへ移動"
            title="最新メッセージまでスクロール"
            onClick={() => {
              setAutoScrollEnabled(true);
              scrollToBottom("smooth");
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="m6 13 6 6 6-6" />
            </svg>
          </button>
        ) : null}
      </main>

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(input);
        }}
      >
        <div className="composer__field">
          <textarea
            className="composer__input"
            ref={textareaRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            disabled={!isSignedIn || isComposerLockedByQuota}
            title={composerLockMessage}
            placeholder={
              !isSignedIn || isComposerLockedByQuota
                ? composerLockMessage
                : "雰囲気や条件を書いてください"
            }
            rows={1}
          />

          <div className="composer__actions">
            <div
              className={`composer__mode-pill ${
                modeIsHeat ? "composer__mode-pill--heat" : "composer__mode-pill--cool"
              }`}
              aria-label={modeLabel}
              title={
                modeIsHeat
                  ? "セッションの期限は最後の送信から5分間です。セッションが切れるまでに送信するとエナジーを節約できます。"
                  : "セッションの期限が切れています。長いセッションでは追加のエナジー消費が発生することがあります。"
              }
            >
              <svg
                className="composer__timer-ring"
                viewBox="0 0 44 44"
                aria-hidden="true"
              >
                <circle
                  className="composer__timer-ring-track"
                  cx="22"
                  cy="22"
                  r={MODE_RING_RADIUS}
                />
                <circle
                  className={`composer__timer-ring-progress ${
                    modeIsHeat
                      ? "composer__timer-ring-progress--heat"
                      : "composer__timer-ring-progress--cool"
                  }`}
                  cx="22"
                  cy="22"
                  r={MODE_RING_RADIUS}
                  strokeDasharray={MODE_RING_CIRCUMFERENCE}
                  strokeDashoffset={timerDashOffset}
                />
              </svg>
              <span className="material-symbols-outlined" aria-hidden="true">
                {modeIcon}
              </span>
            </div>

            <div className="composer__send-pill">
              {isBusy ? (
                <button
                  className="composer__icon-button composer__icon-button--ghost"
                  type="button"
                  onClick={() => stop()}
                  aria-label="停止"
                  title="応答の生成を停止"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="7" y="7" width="10" height="10" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  className="composer__icon-button"
                  disabled={!input.trim() || !isSignedIn || isComposerLockedByQuota}
                  type="submit"
                  aria-label="送信"
                  title={
                    isComposerLockedByQuota
                      ? composerLockMessage
                      : "メッセージを送信"
                  }
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 19V5" />
                    <path d="m6 11 6-6 6 6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <p className="composer__caption">
          AIの生成する回答には誤りが含まれることがあります
        </p>
      </form>
    </div>
  );
}
